﻿import Country from "@model/Country"
import EventBoat from "@model/EventBoat"
import Member from "@model/Member"
import { DateFormat, ds, ts } from '@/plugins/i18nFormat'
import nvl from "@/utils/nvl"
import ClubsRepo from "@repo/ClubsRepo"
import { Model, useRepo } from "pinia-orm"
import { DateCast } from "pinia-orm/casts"
import { BelongsTo, Uid, Str, Num, Bool, Cast, Attr } from "pinia-orm/decorators"

export function crewDump ( crew ) {
    return {
        memberId: crew.memberId,
        captain: crew.captain,
        order: crew.order,
        nonMemberRegId: crew.nonMemberRegId,
        nonMemberFirstName: crew.nonMemberFirstName,
        nonMemberLastName: crew.nonMemberLastName,
        nonMemberSex: crew.nonMemberSex,
        nonMemberBirthDate: crew.nonMemberBirthDate === null ? crew.nonMemberBirthDate : ds( crew.nonMemberBirthDate, DateFormat.SystemDate ),
        nonMemberBirthCode: crew.nonMemberBirthCode,
        agedBirthYear: crew.agedBirthYear,
        nonMemberCitizenship: crew.nonMemberCitizenship,
        nonMemberZip: crew.nonMemberZip,
        nonMemberStreet: crew.nonMemberStreet,
        nonMemberCity: crew.nonMemberCity,
        nonMemberHouseNumber: crew.nonMemberHouseNumber,
        nonMemberOrientationNumber: crew.nonMemberOrientationNumber,
        nonMemberRegistrationNumber: crew.nonMemberRegistrationNumber,
        nonMemberEmail: crew.nonMemberEmail,
        nonMemberPhone: crew.nonMemberPhone,
        note: crew.note,
        wsId: crew.wsId
    }
}

export function crewHash( crewDump ) {
    return JSON.stringify( Object.keys( crewDump ).filter( ( key ) => crewDump[ key ] !== null ).sort().reduce( ( acc, key ) => ( { ...acc, [key]: crewDump[ key ] } ), {} ) )
}

export default class EventCrew extends Model {
    static entity = 'events-crew'

    @Uid() declare id: string
    @Uid() declare eventId: string      // only for sharding
    @Str( '', { notNullable: true } ) declare eventBoatId: string
    @BelongsTo( () => EventBoat, 'eventBoatId' ) declare eventBoat: EventBoat
    @Str( null ) declare memberId: string | null
    @BelongsTo( () => Member, 'memberId' ) declare member: Member
    @Str( null ) declare nonMemberRegId: string | null
    @Str( null ) declare nonMemberFirstName: string | null
    @Str( null ) declare nonMemberLastName: string | null
    @Str( null ) declare nonMemberSex: string | null
    @Cast( () => DateCast ) @Attr( null ) declare nonMemberBirthDate: string | null
    @Str( null ) declare nonMemberBirthCode: string | null
    @Num( null ) declare agedBirthYear: number | null
    @Bool( false, { notNullable: true } ) declare captain: boolean
    @Num( 0, { notNullable: true } ) declare order: number
    @Num( null ) declare nonMemberCitizenship: number | null
    @BelongsTo( () => Country, 'nonMemberCitizenship' ) declare nonMemberCitizenshipCountry: Country
    @Str( null ) declare nonMemberZip: string | null
    @Str( null ) declare nonMemberStreet: string | null
    @Str( null ) declare nonMemberCity: string | null
    @Str( null ) declare nonMemberHouseNumber: string | null
    @Str( null ) declare nonMemberOrientationNumber: string | null
    @Str( null ) declare nonMemberRegistrationNumber: string | null
    @Str( null ) declare nonMemberEmail: string | null
    @Str( null ) declare nonMemberPhone: string | null
    @Str( null ) declare note: string | null
    @Str( null ) declare wsId: string | null

    get dump () {
        return crewDump( this )
    }

    get canImproveToPublic (): boolean {
        const publicMode = this.eventBoat.eventClass?.publicMode ?? 0

        return this.isOneTime && publicMode > 0 && ( !this.captain || publicMode === 2 )
    }

    get canBeCaptain (): boolean {
        return !this.captain && ( !this.isPublic || this.eventBoat.eventClass?.publicMode === 2 )
    }

    get isValid (): boolean {
        return this.eventBoatId !== '' && ( this.memberId !== '' || this.isValidForeign || this.isValidOneTime || this.isValidPublic )
    }

    get isPublic (): boolean {
        return this.nonMemberRegId !== null && this.nonMemberRegId.substring( 0, 4 ) === '8888'
    }

    get isOneTime (): boolean {
        return this.nonMemberRegId !== null && this.nonMemberRegId.substring( 0, 4 ) === '9999'
    }

    get isForeign (): boolean {
        return this.nonMemberRegId !== null && ( this.nonMemberRegId.substring( 0, 4 ) !== '9999' && parseInt( this.nonMemberRegId.substring( 0, 4 ) ) > 9000 )
    }

    get isValidForeign (): boolean {
        return this.isForeign && this.isValidNonMember
    }

    get isValidNonMember (): boolean {
        return this.nonMemberRegId !== null && this.nonMemberRegId.length === 8 && this.nonMemberFirstName !== null && this.nonMemberFirstName !== ''
            && this.nonMemberLastName !== null && this.nonMemberLastName !== '' && ( this.nonMemberSex === 'male' || this.nonMemberSex === 'female' )
    }

    get isValidOneTime (): boolean {
        return this.isOneTime && this.isValidNonMember
    }

    get isValidPublic (): boolean {
        return this.isPublic && this.isValidNonMember
    }

    get regId () {
        return ( this.memberId !== null && this.member !== null ) ? this.member?.regId : this.nonMemberRegId
    }

    get nonMemberAddress (): string | null {
        let streetPart: string | null = null
        if ( this.nonMemberStreet !== null && this.nonMemberStreet !== '' ) {
            if ( this.nonMemberRegistrationNumber !== null && this.nonMemberRegistrationNumber !== '' ) {
                streetPart = ( this.nonMemberHouseNumber !== null && this.nonMemberHouseNumber !== '' ? this.nonMemberHouseNumber + ' ' : '' )
                    + this.nonMemberStreet + ' ' + ts( 'registrationNumberAbbr' ) + ' ' + this.nonMemberRegistrationNumber
            }
            else if ( this.nonMemberOrientationNumber !== null && this.nonMemberOrientationNumber !== '' ) {
                streetPart = this.nonMemberStreet + ' '
                    + ( this.nonMemberHouseNumber !== null && this.nonMemberHouseNumber !== '' ? this.nonMemberHouseNumber + '/' : '' )
                    + this.nonMemberOrientationNumber
            }
            else {
                streetPart = this.nonMemberStreet
                    + ( this.nonMemberHouseNumber !== null && this.nonMemberHouseNumber !== '' ? ' ' + this.nonMemberHouseNumber : '' )
            }
        }

        const zip = ( this.nonMemberZip?.length === 5 ) ? this.nonMemberZip.substring( 0, 3 ) + ' ' + this.nonMemberZip.substring( 3 ) : ( this.nonMemberZip ?? '' )
        const cityPart = ( zip + ' ' + ( this.nonMemberCity ?? '' )
            + ( ( ( this.nonMemberStreet === null || this.nonMemberStreet === '' ) && ( this.nonMemberHouseNumber !== null && this.nonMemberHouseNumber !== '' ) )
                ? ' ' + ts( 'houseNumberAbbr' ) + ' ' + this.nonMemberHouseNumber
                : ''
            ) ).trim()

        const parts = [ streetPart, cityPart ].filter( ( part ) => part !== null && part !== '' )

        return parts.length > 0 ? parts.join( ', ' ) : null
    }

    get firstName () {
        return ( this.memberId !== null && this.member !== null ) ? this.member?.firstName : this.nonMemberFirstName
    }

    get lastName () {
        return ( this.memberId !== null && this.member !== null ) ? this.member?.lastName : this.nonMemberLastName
    }

    get isLicenseIssue () {
        return this.memberId !== null && ( this.member === null || ! this.member.isLicensed )
    }

    get fullName () {
        return this.firstName + " " + this.lastName
    }

    get reverseFullName () {
        return this.lastName + " " + this.firstName
    }

    get reverseShortName () {
        return this.lastName + " " + this.firstName?.toShortPersonalName()
    }

    get sex () {
        return ( this.memberId !== null && this.member !== null ) ? this.member?.sex : this.nonMemberSex
    }

    get email () {
        return ( this.memberId !== null && this.member !== null ) ? this.member?.email : this.nonMemberEmail
    }

    get phone () {
        return ( this.memberId !== null && this.member !== null ) ? this.member?.phone : this.nonMemberPhone
    }

    get birthYear () {
        return this.birthDate?.getFullYear()
    }

    get effectiveBirthYear (): number | undefined {
        return this.agedBirthYear ?? this.birthYear
    }

    get birthDate () {
        if ( this.memberId !== null && this.member !== null ) {
            return this.member?.birthDate !== null ? new Date( this.member?.birthDate ) : null
        }

        return this.nonMemberBirthDate !== null ? new Date( this.nonMemberBirthDate ) : null
    }

    get visibleClub () {
        if ( this.memberId !== null )
            return nvl( this.member?.club?.shortcut, this.member?.club?.name )

        if ( this.nonMemberRegId === null )
            return ''

        if ( this.isOneTime )
            return ts( 'oneTimeLicense' )

        if ( this.isPublic )
            return ts( 'publicLicense' )

        if ( this.isForeign )
            return useRepo( ClubsRepo ).find( parseInt( this.nonMemberRegId.substring( 0, 4 ) ) )?.name

        return ''
    }

    get noc () {
        if ( this.memberId !== null )
            return 'CZE'

        if ( this.nonMemberRegId === null )
            return null

        if ( this.isOneTime || this.isPublic )
            return 'CZE'

        if ( this.isForeign )
            return useRepo( ClubsRepo ).find( parseInt( this.nonMemberRegId.substring( 0, 4 ) ) )?.name.substring( 0, 3 )

        return null
    }

    get age () {
        const currentYear = new Date().getFullYear()

        return currentYear - ( this.birthYear ?? currentYear )
    }

    get effectiveAge (): number {
        const currentYear = new Date().getFullYear()

        return currentYear - ( this.effectiveBirthYear ?? currentYear )
    }

    get isCzech () {
        return !this.isForeign && !this.isPublic
    }

    isFromRegion ( clubId: number | string ): boolean {
        return !this.isForeign && !this.isPublic && !this.isOneTime && this.memberId !== null && this.member?.club?.isSameRegion( clubId )
    }

    isFromDistrict ( clubId: number | string ): boolean {
        return !this.isForeign && !this.isPublic && !this.isOneTime && this.memberId !== null && this.member?.club?.isSameDistrict( clubId )
    }

    static piniaOptions = {
        persist: false
    }
}
